import type PriceClassExtraOption from '../PriceClassExtraOption';
import PriceClass from '@itinerary/PriceClass';
import { BuyExtraFields, PassengerFields } from '@store/form';

import {
    type HotelBreakDownRoomPriceView,
    type HotelResponseRoomPrice,
} from '@clients/HotelApi';

type PriceClassOptions = {
    hasExtraBed: boolean;
    extraBedCapacity: number;
    checkinHour: string;
    checkoutHour: string;
    uniqueId: string;
    onlyThisClassSelectable: boolean;
};

export enum HotelExtraOptions {
    EarlyCheckin = 'earlyCheckin',
    LateCheckout = 'lateCheckout',
}
export default class HotelPriceClass extends PriceClass {
    room: HotelResponseRoomPrice;
    options: PriceClassOptions;
    id: string | null;
    override title = '';
    roomServiceTitle: string[] = [];
    readonly singlePrice: number = 0;
    override netPrice = 0;
    override capacity = 0;
    hasEarlyCheckin = false;
    earlyCheckinPrice = 0;
    hasLateCheckout = false;
    lateCheckoutPrice = 0;
    hasExtraBed = false;
    extraBedPrice = 0;
    extraBedInfantPrice = 0;
    extraBedChildPrice = 0;
    extraBedCapacity = 0;
    breakDownRoomPrices: HotelBreakDownRoomPriceView[] = [];
    checkinHour: string;
    checkoutHour: string;
    uniqueId: string;
    onlyThisClassSelectable = false;
    providerName = '';

    selectedExtraPerson: number[] = [];

    constructor(room: HotelResponseRoomPrice, options: PriceClassOptions) {
        super();
        this.providerName = room.providerName || '';
        this.room = room;
        this.options = options;
        this.id = room.roomId || null;
        this.breakDownRoomPrices = room.breakDownRoomPrices || [];
        this.singlePrice = room.totalPrice ?? 0;
        this.roomServiceTitle = room.roomServiceTitle || [];
        this.netPrice = room.payable ?? 0;
        this.capacity = room.availableRoomCount || 0;
        this.hasEarlyCheckin = room.hasEarlyCheckin || false;
        this.earlyCheckinPrice = room.earlyCheckinPrice || 0;
        this.hasLateCheckout = room.hasLateCheckout || false;
        this.lateCheckoutPrice = room.lateCheckoutPrice || 0;
        this.hasExtraBed = options.hasExtraBed;
        this.extraBedCapacity = options.extraBedCapacity;
        this.extraBedPrice = this.getPriceWithPreDiscount(
            room.extraBedPrice ?? 0
        );
        this.extraBedInfantPrice = room.extraBedInfantPrice || 0;
        this.extraBedChildPrice = room.extraBedChildPrice || 0;
        this.checkinHour = options.checkinHour;
        this.checkoutHour = options.checkoutHour;
        this.uniqueId = options.uniqueId;
        this.onlyThisClassSelectable = options.onlyThisClassSelectable;
    }

    override getAvailableFormExtraOptions(): PriceClassExtraOption[] {
        const list: PriceClassExtraOption[] = [];
        if (this.hasEarlyCheckin) {
            list.push({
                id: HotelExtraOptions.EarlyCheckin,
                title: 'ورود زودهنگام به اتاق',
                description:
                    'در صورت تمایل می‌توانید اتاق خود را در ساعت ۲۱ الی ۲ بعداز ظهر تحویل بگیرید.',
                price: this.earlyCheckinPrice,
            });
        }
        if (this.hasLateCheckout) {
            list.push({
                id: HotelExtraOptions.LateCheckout,
                title: 'خروج دیرهنگام از اتاق',
                description:
                    'در صورت تمایل می‌توانید اتاق خود را در ساعت ۲۱ الی ۲ بعداز ظهر تحویل دهید.',
                price: this.lateCheckoutPrice,
            });
        }
        return list;
    }

    // change name to discount percent
    get percent(): number {
        return this.haveCustomDiscount
            ? Math.ceil(
                  100 - ((this.netPrice || 0) / (this.singlePayable || 0)) * 100
              )
            : Math.ceil(
                  100 -
                      ((this.singlePayable || 0) / (this.singlePrice || 0)) *
                          100
              );
    }

    get discountedPrice(): number | null {
        if (this.haveCustomDiscount) {
            return this.netPrice;
        }

        return (this.singlePayable || 0) < (this.singlePrice || 0)
            ? this.singlePrice || 0
            : null;
    }

    get isFloating() {
        return this.capacity === 0 || !this.singlePayable;
    }

    override get prePriceDiscountPercent() {
        return toValue(this.servicesPreDiscountPercent).Hotel;
    }

    override get descriptionItems(): string[] {
        throw new Error('Method not implemented.');
    }

    override get totalPayableForCheck(): number {
        return (
            this.singlePayable +
            this.selectedExtraPerson.reduce(
                count => count + this.extraBedPrice,
                0
            )
        );
    }

    override get totalPriceForCheck(): number {
        return (
            (this.haveCustomDiscount ? this.netPrice : this.singlePrice) +
            this.selectedExtraPerson.reduce(
                count => count + this.extraBedPrice,
                0
            )
        );
    }

    override get loadableTermsUrl(): string | null {
        throw new Error('Method not implemented.');
    }

    override get cancellationTerms(): string | null {
        throw new Error('Method not implemented.');
    }

    get extraBuyFields(): number {
        if (this.hasEarlyCheckin && this.hasLateCheckout) {
            return (
                BuyExtraFields.HotelLateCheckout |
                BuyExtraFields.HotelEarlyCheckin
            );
        }
        if (this.hasLateCheckout) {
            return BuyExtraFields.HotelLateCheckout;
        }
        if (this.hasEarlyCheckin) {
            return BuyExtraFields.HotelEarlyCheckin;
        }
        return PassengerFields.None;
    }
}
